import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Loader2, Save, Plus, Trash2, GripVertical, ImageIcon, RefreshCw } from 'lucide-react';

export const AdminSettings = () => {
  const { apiCall } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferStatus, setTransferStatus] = useState(null);
  const [settings, setSettings] = useState({
    store_name: { ro: '', en: '' },
    logo: '',
    favicon: '',
    contact_email: '',
    contact_phone: '',
    address: { ro: '', en: '' },
    social_links: { facebook: '', instagram: '', twitter: '', tiktok: '', pinterest: '' },
    currency_rates: { ron: 1, eur: 0.2 },
    tax_rate: 19,
    shipping_cost: { ron: 15, eur: 3 },
    guest_checkout_enabled: true,
    // Analytics
    google_analytics_id: '',
    meta_pixel_id: '',
    // Header Menu
    header_menu: [],
    // Popup settings
    popup_enabled: false,
    popup_delay_seconds: 10,
    popup_title: { ro: '', en: '' },
    popup_content: { ro: '', en: '' },
    popup_button_text: { ro: '', en: '' },
    popup_button_link: '/shop',
    // Stripe settings
    stripe_enabled: false,
    stripe_publishable_key: '',
    stripe_secret_key: '',
    // Email notification settings
    notification_email: '',
    email_notifications_enabled: false,
  });

  useEffect(() => {
    fetchSettings();
    checkTransferStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await apiCall('/admin/settings');
      if (data) {
        setSettings((prev) => ({ ...prev, ...data }));
      }
    } catch (err) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const checkTransferStatus = async () => {
    try {
      const data = await apiCall('/admin/images/transfer-status');
      setTransferStatus(data);
      if (data.status === 'running') {
        setTransferring(true);
        // Poll for updates
        setTimeout(checkTransferStatus, 5000);
      } else {
        setTransferring(false);
      }
    } catch (err) {
      // Ignore errors
    }
  };

  const startImageTransfer = async () => {
    setTransferring(true);
    try {
      await apiCall('/admin/images/transfer-all', { method: 'POST' });
      toast.success('Image transfer started in background');
      // Start polling
      setTimeout(checkTransferStatus, 5000);
    } catch (err) {
      toast.error('Failed to start image transfer');
      setTransferring(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiCall('/admin/settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      <div>
        <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Settings</h2>
        <p className="text-muted-foreground">Configure your store</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Store Info */}
        <Card>
          <CardHeader>
            <CardTitle>Store Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Store Name (RO)</Label>
                <Input
                  value={settings.store_name?.ro || ''}
                  onChange={(e) => setSettings({ ...settings, store_name: { ...settings.store_name, ro: e.target.value } })}
                />
              </div>
              <div>
                <Label>Store Name (EN)</Label>
                <Input
                  value={settings.store_name?.en || ''}
                  onChange={(e) => setSettings({ ...settings, store_name: { ...settings.store_name, en: e.target.value } })}
                />
              </div>
            </div>
            <div>
              <Label>Logo URL</Label>
              <Input
                value={settings.logo || ''}
                onChange={(e) => setSettings({ ...settings, logo: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={settings.contact_email || ''}
                  onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={settings.contact_phone || ''}
                  onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Address (RO)</Label>
              <Input
                value={settings.address?.ro || ''}
                onChange={(e) => setSettings({ ...settings, address: { ...settings.address, ro: e.target.value } })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Social */}
        <Card>
          <CardHeader>
            <CardTitle>Social Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Facebook</Label>
                <Input
                  placeholder="https://facebook.com/yourpage"
                  value={settings.social_links?.facebook || ''}
                  onChange={(e) => setSettings({ ...settings, social_links: { ...settings.social_links, facebook: e.target.value } })}
                />
              </div>
              <div>
                <Label>Instagram</Label>
                <Input
                  placeholder="https://instagram.com/yourpage"
                  value={settings.social_links?.instagram || ''}
                  onChange={(e) => setSettings({ ...settings, social_links: { ...settings.social_links, instagram: e.target.value } })}
                />
              </div>
              <div>
                <Label>TikTok</Label>
                <Input
                  placeholder="https://tiktok.com/@yourpage"
                  value={settings.social_links?.tiktok || ''}
                  onChange={(e) => setSettings({ ...settings, social_links: { ...settings.social_links, tiktok: e.target.value } })}
                />
              </div>
              <div>
                <Label>Pinterest</Label>
                <Input
                  placeholder="https://pinterest.com/yourpage"
                  value={settings.social_links?.pinterest || ''}
                  onChange={(e) => setSettings({ ...settings, social_links: { ...settings.social_links, pinterest: e.target.value } })}
                />
              </div>
              <div>
                <Label>Twitter / X</Label>
                <Input
                  placeholder="https://twitter.com/yourpage"
                  value={settings.social_links?.twitter || ''}
                  onChange={(e) => setSettings({ ...settings, social_links: { ...settings.social_links, twitter: e.target.value } })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card>
          <CardHeader>
            <CardTitle>Pricing & Tax</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  value={settings.tax_rate || 0}
                  onChange={(e) => setSettings({ ...settings, tax_rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Shipping (RON)</Label>
                <Input
                  type="number"
                  value={settings.shipping_cost?.ron || 0}
                  onChange={(e) => setSettings({ ...settings, shipping_cost: { ...settings.shipping_cost, ron: parseFloat(e.target.value) || 0 } })}
                />
              </div>
              <div>
                <Label>Shipping (EUR)</Label>
                <Input
                  type="number"
                  value={settings.shipping_cost?.eur || 0}
                  onChange={(e) => setSettings({ ...settings, shipping_cost: { ...settings.shipping_cost, eur: parseFloat(e.target.value) || 0 } })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Checkout */}
        <Card>
          <CardHeader>
            <CardTitle>Checkout Options</CardTitle>
          </CardHeader>
          <CardContent>
            <label className="flex items-center gap-2">
              <Switch
                checked={settings.guest_checkout_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, guest_checkout_enabled: checked })}
              />
              <span>Allow guest checkout (order without account)</span>
            </label>
          </CardContent>
        </Card>

        {/* Analytics & Tracking */}
        <Card>
          <CardHeader>
            <CardTitle>Analytics & Tracking</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Google Analytics ID</Label>
                <Input
                  placeholder="G-XXXXXXXXXX"
                  value={settings.google_analytics_id || ''}
                  onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">Enter your GA4 Measurement ID</p>
              </div>
              <div>
                <Label>Meta Pixel ID</Label>
                <Input
                  placeholder="1234567890"
                  value={settings.meta_pixel_id || ''}
                  onChange={(e) => setSettings({ ...settings, meta_pixel_id: e.target.value })}
                />
                <p className="text-xs text-muted-foreground mt-1">Enter your Facebook/Meta Pixel ID</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Image Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Image Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Transfer all external product images (from WordPress, Unsplash, etc.) to local storage for better performance and reliability.
            </p>
            
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={startImageTransfer}
                disabled={transferring}
              >
                {transferring ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Transferring...
                  </>
                ) : (
                  <>
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Transfer All External Images
                  </>
                )}
              </Button>
              
              {transferStatus && (
                <div className="text-sm">
                  {transferStatus.status === 'running' && (
                    <span className="text-yellow-600">Transfer in progress...</span>
                  )}
                  {transferStatus.status === 'completed' && (
                    <span className="text-green-600">
                      Last transfer: {transferStatus.transferred} images transferred, {transferStatus.failed} failed
                    </span>
                  )}
                  {transferStatus.status === 'not_started' && (
                    <span className="text-muted-foreground">No transfer has been started yet</span>
                  )}
                </div>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground">
              Note: This process runs in the background. Large catalogs may take several minutes.
            </p>
          </CardContent>
        </Card>

        {/* Header Menu Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Dynamic Header Menu</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Configure custom menu items. Leave empty to use default category-based menu.
            </p>
            {(settings.header_menu || []).map((item, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Menu Item {index + 1}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-500"
                    onClick={() => {
                      const newMenu = [...(settings.header_menu || [])];
                      newMenu.splice(index, 1);
                      setSettings({ ...settings, header_menu: newMenu });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Label (RO)"
                    value={item.label?.ro || ''}
                    onChange={(e) => {
                      const newMenu = [...(settings.header_menu || [])];
                      newMenu[index] = { ...newMenu[index], label: { ...newMenu[index].label, ro: e.target.value } };
                      setSettings({ ...settings, header_menu: newMenu });
                    }}
                  />
                  <Input
                    placeholder="Label (EN)"
                    value={item.label?.en || ''}
                    onChange={(e) => {
                      const newMenu = [...(settings.header_menu || [])];
                      newMenu[index] = { ...newMenu[index], label: { ...newMenu[index].label, en: e.target.value } };
                      setSettings({ ...settings, header_menu: newMenu });
                    }}
                  />
                </div>
                <Input
                  placeholder="Link (e.g., /shop?category=offers)"
                  value={item.link || ''}
                  onChange={(e) => {
                    const newMenu = [...(settings.header_menu || [])];
                    newMenu[index] = { ...newMenu[index], link: e.target.value };
                    setSettings({ ...settings, header_menu: newMenu });
                  }}
                />
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSettings({
                  ...settings,
                  header_menu: [...(settings.header_menu || []), { label: { ro: '', bg: '', en: '' }, link: '' }]
                });
              }}
            >
              <Plus className="h-4 w-4 mr-2" /> Add Menu Item
            </Button>
          </CardContent>
        </Card>

        {/* Popup Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Promotional Popup</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-center gap-2">
              <Switch
                checked={settings.popup_enabled || false}
                onCheckedChange={(checked) => setSettings({ ...settings, popup_enabled: checked })}
              />
              <span>Enable popup for visitors</span>
            </label>

            <div>
              <Label>Delay (seconds)</Label>
              <Input
                type="number"
                min="1"
                max="300"
                value={settings.popup_delay_seconds || 10}
                onChange={(e) => setSettings({ ...settings, popup_delay_seconds: parseInt(e.target.value) || 10 })}
              />
              <p className="text-xs text-muted-foreground mt-1">Time before popup appears</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title (RO)</Label>
                <Input
                  value={settings.popup_title?.ro || ''}
                  onChange={(e) => setSettings({ ...settings, popup_title: { ...settings.popup_title, ro: e.target.value } })}
                />
              </div>
              <div>
                <Label>Title (EN)</Label>
                <Input
                  value={settings.popup_title?.en || ''}
                  onChange={(e) => setSettings({ ...settings, popup_title: { ...settings.popup_title, en: e.target.value } })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Content (RO)</Label>
                <Textarea
                  rows={2}
                  value={settings.popup_content?.ro || ''}
                  onChange={(e) => setSettings({ ...settings, popup_content: { ...settings.popup_content, ro: e.target.value } })}
                />
              </div>
              <div>
                <Label>Content (EN)</Label>
                <Textarea
                  rows={2}
                  value={settings.popup_content?.en || ''}
                  onChange={(e) => setSettings({ ...settings, popup_content: { ...settings.popup_content, en: e.target.value } })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Button Text (RO)</Label>
                <Input
                  value={settings.popup_button_text?.ro || ''}
                  onChange={(e) => setSettings({ ...settings, popup_button_text: { ...settings.popup_button_text, ro: e.target.value } })}
                />
              </div>
              <div>
                <Label>Button Text (EN)</Label>
                <Input
                  value={settings.popup_button_text?.en || ''}
                  onChange={(e) => setSettings({ ...settings, popup_button_text: { ...settings.popup_button_text, en: e.target.value } })}
                />
              </div>
            </div>

            <div>
              <Label>Button Link</Label>
              <Input
                placeholder="/shop?category=offers"
                value={settings.popup_button_link || ''}
                onChange={(e) => setSettings({ ...settings, popup_button_link: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Stripe Payment Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Stripe Payment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-center gap-2">
              <Switch
                checked={settings.stripe_enabled || false}
                onCheckedChange={(checked) => setSettings({ ...settings, stripe_enabled: checked })}
              />
              <span>Enable Stripe Payments</span>
            </label>
            
            <div>
              <Label>Publishable Key</Label>
              <Input
                placeholder="pk_test_..."
                value={settings.stripe_publishable_key || ''}
                onChange={(e) => setSettings({ ...settings, stripe_publishable_key: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">Your Stripe publishable key (starts with pk_)</p>
            </div>
            
            <div>
              <Label>Secret Key</Label>
              <Input
                type="password"
                placeholder="sk_test_..."
                value={settings.stripe_secret_key || ''}
                onChange={(e) => setSettings({ ...settings, stripe_secret_key: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">Your Stripe secret key (starts with sk_) - kept secure</p>
            </div>
          </CardContent>
        </Card>

        {/* Email Notifications Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Email Notifications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-center gap-2">
              <Switch
                checked={settings.email_notifications_enabled || false}
                onCheckedChange={(checked) => setSettings({ ...settings, email_notifications_enabled: checked })}
              />
              <span>Enable Email Notifications</span>
            </label>
            
            <div>
              <Label>Notification Email</Label>
              <Input
                type="email"
                placeholder="office@ranistore.ro"
                value={settings.notification_email || ''}
                onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">Email address to receive notifications for new orders, newsletter subscriptions, etc.</p>
            </div>
          </CardContent>
        </Card>

        <Button type="submit" disabled={saving} className="bg-primary">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Settings
        </Button>
      </form>
    </div>
  );
};

export default AdminSettings;
